// Communities (groups of groups) and broadcast lists.
import { signal } from "@preact/signals";
import { useLayoutEffect, useState } from "preact/hooks";
import { api, type Community } from "../api";
import * as S from "../state";
import { Avatar, uploadAvatar } from "./Avatar";
import { ContactPicker } from "./Groups";
import { Icon } from "./Icon";
import { drawer, engine, infoOpen, toast } from "./Main";
import { Confirm } from "./Panels";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function useEscape(fn: () => void) {
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && fn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fn]);
}

/** Community shown in the "community" drawer. */
export const openCommunity = signal<string | null>(null);

function replace(c: Community) {
  S.communities.value = [...S.communities.value.filter((x) => x.id !== c.id), c].sort((a, b) => a.name.localeCompare(b.name));
}

export function CommunitiesTab() {
  const list = S.communities.value;
  return (
    <div class="tab-body">
      <div class="status-actions pad-x">
        <button class="chip" onClick={() => (drawer.value = "newCommunity")}>
          <Icon name="plus" size={16} /> New community
        </button>
      </div>
      <ul class="chat-list" aria-label="Communities">
        {list.map((c) => (
          <li
            key={c.id}
            class="chat-row"
            tabIndex={0}
            onClick={() => ((openCommunity.value = c.id), (drawer.value = "community"))}
            onKeyDown={(e) => e.key === "Enter" && ((openCommunity.value = c.id), (drawer.value = "community"))}
          >
            <Avatar url={c.avatar_url} jid={c.id} name={c.name} size={49} group />
            <div class="chat-row-main">
              <div class="chat-row-top">
                <span class="chat-name">{c.name}</span>
              </div>
              <div class="chat-row-bottom">
                <span class="chat-last">
                  {c.groups.length} group{c.groups.length === 1 ? "" : "s"} · {c.members} member{c.members === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          </li>
        ))}
        {!list.length && (
          <li class="muted pad small">
            Communities bring members together in topic-based groups and make it easy to get admin announcements. Any community you're added to appears here.
          </li>
        )}
      </ul>
    </div>
  );
}

export function NewCommunity() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState<{ id: string; url: string } | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const back = () => (drawer.value = "none");
  useEscape(back);
  // Groups we administer that aren't in a community yet.
  const mine = [...S.groups.value.values()].filter((g) => !g.left && !g.community && (g.my_role === "owner" || g.my_role === "admin"));
  async function create() {
    setBusy(true);
    try {
      const c = await api.createCommunity({ name: name.trim(), description: description.trim(), avatar_media_id: icon?.id, groups: picked });
      replace(c);
      void engine.value?.loadGroups();
      openCommunity.value = c.id;
      drawer.value = "community";
    } catch (e) {
      toast(errText(e));
      setBusy(false);
    }
  }
  return (
    <div class="drawer" role="dialog" aria-label="New community">
      <header class="drawer-head">
        <button class="icon-btn" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>New community</h2>
      </header>
      <div class="drawer-body">
        <div class="new-group-details">
          <label class="group-icon-pick">
            <Avatar url={icon?.url} jid="new-community" name={name} size={120} group />
            <span class="avatar-picker-overlay">
              <Icon name="camera" size={24} />
              Add icon
            </span>
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) void uploadAvatar(f).then((u) => setIcon({ id: u.id, url: u.url }), (err) => toast(errText(err)));
              }}
            />
          </label>
          <input class="line-input" value={name} maxLength={100} onInput={(e) => setName(e.currentTarget.value)} placeholder="Community name" aria-label="Community name" autoFocus />
          <textarea class="line-input" rows={2} value={description} maxLength={2048} onInput={(e) => setDescription(e.currentTarget.value)} placeholder="Description" aria-label="Community description" />
        </div>
        <p class="muted pad small">Add groups you're an admin of. Their members join the community and get its announcements.</p>
        <ul class="plain-list pick-groups">
          {mine.map((g) => (
            <li key={g.jid}>
              <label class="radio-row">
                <input type="checkbox" checked={picked.includes(g.jid)} onChange={() => setPicked((p) => (p.includes(g.jid) ? p.filter((x) => x !== g.jid) : [...p, g.jid]))} aria-label={g.name} />
                <Avatar url={g.avatar_url} jid={g.jid} name={g.name} size={36} group />
                <span class="grow">{g.name}</span>
                <small class="muted">{g.members.length}</small>
              </label>
            </li>
          ))}
          {!mine.length && <li class="muted pad small">You don't administer any groups yet; you can add or create groups later.</li>}
        </ul>
        <div class="pad">
          <button class="primary" disabled={!name.trim() || busy} onClick={() => void create()}>
            Create community
          </button>
        </div>
      </div>
    </div>
  );
}

export function CommunityPanel() {
  const c = S.communities.value.find((x) => x.id === openCommunity.value);
  const e = engine.value!;
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmLeave, setConfirmLeave] = useState(false);
  const back = () => (drawer.value = "none");
  useEscape(back);
  if (!c) return null;
  const admin = c.my_role === "admin";
  const mine = [...S.groups.value.values()].filter((g) => !g.left && !g.community && (g.my_role === "owner" || g.my_role === "admin"));
  const open = async (jid: string) => {
    back();
    S.tab.value = "chats";
    await e.openChat(jid);
  };
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      toast(errText(err));
    }
  };
  return (
    <div class="drawer" role="dialog" aria-label={"Community " + c.name}>
      <header class="drawer-head">
        <button class="icon-btn" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>{c.name}</h2>
      </header>
      <div class="drawer-body">
        <div class="info-hero">
          <Avatar url={c.avatar_url} jid={c.id} name={c.name} size={96} group />
          <p class="muted">
            Community · {c.members} member{c.members === 1 ? "" : "s"}
          </p>
          {c.description && <p class="pad small">{c.description}</p>}
        </div>
        <ul class="plain-list community-groups">
          <li class="community-group announce" tabIndex={0} onClick={() => void open(c.announce)}>
            <span class="list-action-icon">
              <Icon name="bell" />
            </span>
            <span class="grow">
              <span class="chat-name">Announcements</span>
              <small class="muted">Only admins can send messages</small>
            </span>
          </li>
          <li class="list-heading">Groups in this community</li>
          {c.groups.map((g) => (
            <li key={g.jid} class="community-group">
              <Avatar url={g.avatar_url} jid={g.jid} name={g.name} size={40} group />
              <span class="grow">
                <span class="chat-name">{g.name}</span>
                <small class="muted">
                  {g.members} member{g.members === 1 ? "" : "s"}
                </small>
              </span>
              {g.joined ? (
                <button class="chip" onClick={() => void open(g.jid)}>
                  Open
                </button>
              ) : (
                <button
                  class="chip on"
                  aria-label={"Join " + g.name}
                  onClick={() =>
                    void act(async () => {
                      await api.joinCommunityGroup(c.id, g.jid);
                      await e.loadGroups();
                      await e.loadCommunities();
                      toast(`You joined ${g.name}`);
                    })
                  }
                >
                  Join
                </button>
              )}
              {admin && (
                <button
                  class="icon-btn small"
                  aria-label={"Remove " + g.name + " from community"}
                  onClick={() => void act(async () => replace(await api.removeCommunityGroup(c.id, g.jid)))}
                >
                  <Icon name="close" size={16} />
                </button>
              )}
            </li>
          ))}
          {!c.groups.length && <li class="muted pad small">No groups yet.</li>}
        </ul>
        <div class="info-actions">
          {admin && !adding && (
            <button class="list-action" onClick={() => setAdding(true)}>
              <span class="list-action-icon">
                <Icon name="plus" />
              </span>
              Add group
            </button>
          )}
          {adding && (
            <div class="pad community-add">
              <input class="line-input" value={newName} maxLength={100} onInput={(ev) => setNewName(ev.currentTarget.value)} placeholder="New group name" aria-label="New group name" />
              <button
                class="primary"
                disabled={!newName.trim()}
                onClick={() =>
                  void act(async () => {
                    replace(await api.addCommunityGroup(c.id, { name: newName.trim() }));
                    setNewName("");
                    setAdding(false);
                    await e.loadGroups();
                  })
                }
              >
                Create
              </button>
              {mine.length > 0 && <p class="muted small">Or add a group you administer:</p>}
              {mine.map((g) => (
                <button
                  key={g.jid}
                  class="chip"
                  onClick={() =>
                    void act(async () => {
                      replace(await api.addCommunityGroup(c.id, { group: g.jid }));
                      setAdding(false);
                      await e.loadGroups();
                    })
                  }
                >
                  {g.name}
                </button>
              ))}
            </div>
          )}
          <button class="list-action danger" onClick={() => setConfirmLeave(true)}>
            <span class="list-action-icon">
              <Icon name="exit" />
            </span>
            Exit community
          </button>
        </div>
      </div>
      {confirmLeave && (
        <Confirm
          text={`Exit "${c.name}"? You'll leave its announcements and all its groups.`}
          action="Exit"
          onCancel={() => setConfirmLeave(false)}
          onConfirm={() =>
            void act(async () => {
              setConfirmLeave(false);
              await api.leaveCommunity(c.id);
              S.communities.value = S.communities.value.filter((x) => x.id !== c.id);
              await e.loadGroups();
              back();
            })
          }
        />
      )}
    </div>
  );
}

// ---- broadcast lists -----------------------------------------------------------------------

export function NewBroadcast() {
  const [picked, setPicked] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const back = () => (drawer.value = "none");
  useEscape(back);
  async function create() {
    setBusy(true);
    try {
      const jid = await engine.value!.saveBroadcast(name.trim(), picked);
      back();
      await engine.value!.openChat(jid);
    } catch (e) {
      toast(errText(e));
      setBusy(false);
    }
  }
  return (
    <div class="drawer" role="dialog" aria-label="New broadcast">
      <header class="drawer-head">
        <button class="icon-btn" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>New broadcast</h2>
      </header>
      <div class="drawer-body">
        <p class="muted pad small">Only contacts with your number in their address book will receive your broadcast messages. Each gets it as a normal, end-to-end encrypted chat message.</p>
        <input class="line-input pad-x" value={name} maxLength={100} onInput={(e) => setName(e.currentTarget.value)} placeholder="List name (optional)" aria-label="Broadcast list name" />
        <ContactPicker picked={picked} onChange={setPicked} />
      </div>
      <div class="drawer-fab">
        {picked.length >= 2 && (
          <button class="icon-btn send-fab" disabled={busy} onClick={() => void create()} aria-label="Create broadcast list">
            <Icon name="check" />
          </button>
        )}
      </div>
    </div>
  );
}

export function BroadcastIcon({ size = 49 }: { size?: number }) {
  return (
    <span class="broadcast-icon" style={{ width: `${size}px`, height: `${size}px` }}>
      <Icon name="send" size={Math.round(size / 2.2)} />
    </span>
  );
}

export function broadcastName(jid: string) {
  const b = S.broadcasts.value.get(jid);
  if (!b) return "Broadcast list";
  return b.name || b.members.map((m) => S.displayName(m.jid)).join(", ");
}

export function BroadcastStatusLine({ jid }: { jid: string }) {
  const b = S.broadcasts.value.get(jid);
  const n = b?.members.length ?? 0;
  return <span class="status">{n} recipient{n === 1 ? "" : "s"}</span>;
}

export function BroadcastInfo({ jid }: { jid: string }) {
  const b = S.broadcasts.value.get(jid);
  const [confirm, setConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<string[]>(b?.members.map((m) => m.jid) ?? []);
  if (!b) return null;
  return (
    <aside class="info" aria-label="Broadcast list info">
      <header class="bar">
        <button class="icon-btn" onClick={() => (infoOpen.value = false)} aria-label="Close">
          <Icon name="close" />
        </button>
        <h2>Broadcast list info</h2>
      </header>
      <div class="info-body">
        <div class="info-hero">
          <span class="broadcast-icon big">
            <Icon name="send" size={48} />
          </span>
          <h3>{broadcastName(jid)}</h3>
          <p class="muted">
            {b.members.length} recipient{b.members.length === 1 ? "" : "s"}
          </p>
        </div>
        {editing ? (
          <>
            <ContactPicker picked={picked} onChange={setPicked} />
            <div class="row-buttons pad">
              <button class="link" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button
                class="primary"
                disabled={picked.length < 2}
                onClick={() =>
                  void engine.value!.saveBroadcast(b.name, picked, b.id).then(
                    () => setEditing(false),
                    (e) => toast(errText(e)),
                  )
                }
              >
                Save
              </button>
            </div>
          </>
        ) : (
          <ul class="plain-list">
            {b.members.map((m) => (
              <li key={m.jid}>
                <Avatar url={S.profiles.value.get(m.jid)?.avatar_url} jid={m.jid} name={S.displayName(m.jid)} size={36} />
                <span class="grow">{S.displayName(m.jid)}</span>
                {!m.receives && <small class="muted">Won't receive: hasn't saved your number</small>}
              </li>
            ))}
          </ul>
        )}
        <div class="info-actions">
          {!editing && (
            <button class="list-action" onClick={() => setEditing(true)}>
              <span class="list-action-icon">
                <Icon name="edit" />
              </span>
              Edit recipients
            </button>
          )}
          <button class="list-action danger" onClick={() => setConfirm(true)}>
            <span class="list-action-icon">
              <Icon name="trash" />
            </span>
            Delete broadcast list
          </button>
        </div>
      </div>
      {confirm && (
        <Confirm
          text="Delete this broadcast list? Messages already sent stay in each chat."
          action="Delete"
          onCancel={() => setConfirm(false)}
          onConfirm={() => {
            setConfirm(false);
            infoOpen.value = false;
            void engine.value!.deleteChat(jid).catch((e) => toast(errText(e)));
          }}
        />
      )}
    </aside>
  );
}
